import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, Trash2, Loader2, Download, History, RotateCcw, Save } from "lucide-react";

/* ============ DATABASE ============ */
const DB_LABELS = {
  settings: "Inställningar", categories: "Kategorier", products: "Produkter",
  orders: "Beställningar", promo_codes: "Rabattkoder", inventory_items: "Lagervaror",
  inventory_movements: "Lagerrörelser", inventory_shipments: "Inkommande leveranser",
  counters: "Räknare", accounting: "Bokföring",
  admin_users: "Personal (admin)", users: "Kunder", ai_insights: "AI-insikter",
  messages: "Meddelanden", ai_actions: "AI-åtgärder", events: "Händelselogg",
  proposals: "Förslag", payments: "Betalningar", invoices: "Fakturor",
  bookings: "Bokningar", suppliers: "Leverantörer", documents: "Dokument",
  inquiries: "Förfrågningar", quotes: "Offerter",
};
const DB_DELETABLE = new Set(["orders", "users", "accounting", "promo_codes",
  "inventory_movements", "inventory_shipments", "ai_insights", "messages", "ai_actions"]);

function summarizeDoc(coll, d) {
  if (coll === "orders") return { title: `#${d.order_number ?? "?"} · ${d.customer_name || "Kund"}`, sub: `${d.total ?? "?"} kr · ${d.status || ""} · ${(d.created_at || "").slice(0, 16).replace("T", " ")}` };
  if (coll === "users") return { title: d.full_name || d.email || "Kund", sub: [d.email, d.phone].filter(Boolean).join(" · ") };
  if (coll === "promo_codes") return { title: d.code || d.id, sub: d.description || "" };
  if (coll === "accounting") return { title: d.name || d.type || "Post", sub: `${d.amount ?? ""} kr · ${d.date || ""}` };
  if (coll === "inventory_movements") return { title: d.item_name || d.name || "Rörelse", sub: `${(d.created_at || d.timestamp || "").slice(0, 16).replace("T", " ")}` };
  return { title: d.name || d.label || d.id || "(post)", sub: d.id ? `id: ${String(d.id).slice(0, 8)}…` : "" };
}
const fmtDT = (s) => (s || "").slice(0, 16).replace("T", " ");

export default function DatabasePanel() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [picked, setPicked] = useState([]);          // selected doc ids
  const [busy, setBusy] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapBusy, setSnapBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);       // { title, body, label, danger, onConfirm }

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/admin/database/overview"); setOverview(res.data); }
    catch { toast.error("Kunde inte ladda databasen"); }
    finally { setLoading(false); }
  }, []);
  const loadSnapshots = useCallback(async () => {
    try { const res = await api.get("/admin/database/snapshots"); setSnapshots(res.data.snapshots || []); }
    catch { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); loadSnapshots(); }, [load, loadSnapshots]);

  const viewCollection = async (name) => {
    setSelected(name); setDocs(null); setLoadingDocs(true); setPicked([]); setShowRaw(false);
    try { const res = await api.get(`/admin/database/collection/${name}?limit=200`); setDocs(res.data.documents); }
    catch { toast.error("Kunde inte ladda " + name); }
    finally { setLoadingDocs(false); }
  };

  const downloadBackup = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/database/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `biten-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup nedladdad");
    } catch { toast.error("Kunde inte ladda ner backup"); }
    finally { setExporting(false); }
  };

  const createSnapshot = async () => {
    setSnapBusy(true);
    try {
      await api.post("/admin/database/snapshot", { label: "Manuell återställningspunkt" });
      toast.success("Återställningspunkt skapad");
      loadSnapshots();
    } catch { toast.error("Kunde inte skapa återställningspunkt"); }
    finally { setSnapBusy(false); }
  };

  const restoreSnapshot = (snap) => setConfirm({
    title: "Återställ databasen?",
    body: `Detta ersätter ALL nuvarande data med ögonblicksbilden från ${fmtDT(snap.created_at)} (${snap.total} poster). En automatisk återställningspunkt av nuläget sparas först, så du kan ångra även detta.`,
    label: "Återställ",
    danger: true,
    onConfirm: async () => {
      try {
        await api.post("/admin/database/restore", { snapshot_id: snap.snapshot_id });
        toast.success("Databasen återställd");
        setSelected(null); setDocs(null);
        load(); loadSnapshots();
      } catch { toast.error("Kunde inte återställa"); }
    },
  });

  const deleteSnapshot = (snap) => setConfirm({
    title: "Ta bort återställningspunkt?",
    body: `Detta tar bort återställningspunkten från ${fmtDT(snap.created_at)}. Databasen påverkas inte.`,
    label: "Ta bort",
    danger: true,
    onConfirm: async () => {
      try { await api.delete(`/admin/database/snapshot/${snap.snapshot_id}`); loadSnapshots(); }
      catch { toast.error("Kunde inte ta bort"); }
    },
  });

  const runDelete = (all) => {
    const n = all ? (docs ? docs.length : 0) : picked.length;
    const shared = selected === "users";
    setConfirm({
      title: all ? `Ta bort alla i ${DB_LABELS[selected]}?` : `Ta bort ${n} markerade?`,
      body: `${all ? `Alla ${n} poster i ${DB_LABELS[selected]}` : `${n} valda poster`} tas bort.${shared ? " OBS: Kunder delas mellan alla restauranger – de försvinner överallt." : ""} En automatisk återställningspunkt sparas först, så du kan ångra.`,
      label: "Ta bort",
      danger: true,
      onConfirm: async () => {
        try {
          const body = all ? { collection: selected, all: true } : { collection: selected, ids: picked };
          const res = await api.post("/admin/database/delete", body);
          toast.success(`${res.data.deleted} poster borttagna`);
          await viewCollection(selected);
          load(); loadSnapshots();
        } catch (e) { toast.error(e?.response?.data?.detail || "Kunde inte ta bort"); }
      },
    });
  };

  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const allPicked = docs && docs.length > 0 && picked.length === docs.length;
  const canDelete = selected && DB_DELETABLE.has(selected);

  return (
    <div className="max-w-5xl" data-testid="database-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Databas</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">All data för {overview?.location || "denna restaurang"} – med ångra-skydd.</p>
        </div>
        <Button onClick={downloadBackup} disabled={exporting} variant="outline" className="border-[#3F3F46] text-white hover:bg-[#1A1A1A]">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Ladda ner (JSON)
        </Button>
      </div>

      {/* Restore points */}
      <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2"><History className="w-4 h-4 text-[#D4B483]" /> Återställningspunkter</h3>
            <p className="text-[#C0C0C6] text-xs mt-1">En kopia sparas automatiskt före varje borttagning. Du kan också spara en när som helst, och rulla tillbaka.</p>
          </div>
          <Button onClick={createSnapshot} disabled={snapBusy} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            {snapBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Skapa återställningspunkt
          </Button>
        </div>
        {snapshots.length === 0 ? (
          <p className="text-[#9A9AA2] text-xs py-2">Inga återställningspunkter ännu.</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-auto">
            {snapshots.map(s => (
              <div key={s.snapshot_id} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#1F1F22] rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate flex items-center gap-2">
                    {s.auto && <span className="text-[9px] uppercase tracking-wider bg-[#27272A] text-[#C0C0C6] px-1.5 py-0.5 rounded">Auto</span>}
                    {s.label}
                  </p>
                  <p className="text-[11px] text-[#9A9AA2]">{fmtDT(s.created_at)} · {s.total} poster</p>
                </div>
                <button onClick={() => restoreSnapshot(s)} className="text-xs font-medium text-[#D4B483] hover:text-[#E5CFA5] flex items-center gap-1">
                  <RotateCcw className="w-3.5 h-3.5" /> Återställ
                </button>
                <button onClick={() => deleteSnapshot(s)} className="text-[#71717A] hover:text-[#EF4444]" title="Ta bort">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(overview?.collections || []).map(c => (
            <button key={c.collection} onClick={() => viewCollection(c.collection)}
              className={`text-left bg-[#121212] rounded-xl border p-4 transition-all ${selected === c.collection ? "border-[#D4B483]" : "border-[#27272A] hover:border-[#3F3F46]"}`}>
              <p className="text-[11px] uppercase tracking-wider text-[#C8C8CE]">{DB_LABELS[c.collection] || c.collection}</p>
              <p className="font-playfair text-2xl font-bold text-white mt-1">{c.count}</p>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="mt-6 bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="text-sm font-semibold text-white">{DB_LABELS[selected] || selected} <span className="text-[#9A9AA2] font-normal">— {docs ? docs.length : 0} visade</span></h3>
            <div className="flex items-center gap-2">
              {canDelete && docs && docs.length > 0 && (
                <button onClick={() => setShowRaw(r => !r)} className="text-xs text-[#C0C0C6] hover:text-white">{showRaw ? "Visa lista" : "Visa rådata"}</button>
              )}
              <button onClick={() => { setSelected(null); setDocs(null); }} className="text-[#C8C8CE] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {!canDelete && (
            <p className="text-[11px] text-[#C0C0C6] mb-3 bg-[#0A0A0A] rounded-lg px-3 py-2 border border-[#1F1F22]">Skrivskyddad här – hanteras i sin egen flik (t.ex. Meny, Inställningar, Lager).</p>
          )}

          {loadingDocs ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#D4B483] animate-spin" /></div>
          ) : (docs && docs.length === 0) ? (
            <p className="text-[#9A9AA2] text-sm py-4">Inga poster.</p>
          ) : (canDelete && !showRaw) ? (
            <>
              <div className="flex items-center gap-3 pb-2 mb-2 border-b border-[#1F1F22]">
                <label className="flex items-center gap-2 text-xs text-[#C0C0C6] cursor-pointer">
                  <input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? [] : docs.map(d => d.id))} className="accent-[#D4B483]" />
                  Markera alla
                </label>
                <div className="flex-1" />
                {picked.length > 0 && (
                  <Button onClick={() => runDelete(false)} disabled={busy} size="sm" className="bg-[#EF4444] hover:bg-[#DC2626] text-white h-8">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Ta bort markerade ({picked.length})
                  </Button>
                )}
                <Button onClick={() => runDelete(true)} disabled={busy} size="sm" variant="outline" className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8">
                  Ta bort alla
                </Button>
              </div>
              <div className="space-y-1 max-h-[55vh] overflow-auto">
                {docs.map(d => {
                  const s = summarizeDoc(selected, d);
                  return (
                    <div key={d.id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#0A0A0A]">
                      <input type="checkbox" checked={picked.includes(d.id)} onChange={() => togglePick(d.id)} className="accent-[#D4B483]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{s.title}</p>
                        {s.sub && <p className="text-[11px] text-[#9A9AA2] truncate">{s.sub}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <pre className="text-[11px] text-[#D8D8DC] bg-[#0A0A0A] rounded-lg p-3 overflow-auto max-h-[55vh] leading-relaxed">{JSON.stringify(docs, null, 2)}</pre>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-white">{confirm?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#D8D8DC] leading-relaxed">{confirm?.body}</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-[#3F3F46] text-white hover:bg-[#1A1A1A]" onClick={() => setConfirm(null)}>Avbryt</Button>
            <Button
              disabled={busy}
              className={confirm?.danger ? "bg-[#EF4444] hover:bg-[#DC2626] text-white" : "bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"}
              onClick={async () => { setBusy(true); try { await confirm.onConfirm(); } finally { setBusy(false); setConfirm(null); } }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{confirm?.label || "Bekräfta"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
